import { Button, Menu, MenuItem, TextField, Tooltip } from '@mui/material';
import { NodeProps } from '@xyflow/react';
import { useDispatch, useSelector } from 'react-redux';
import { useState } from 'react';
import {
  duplicateTable,
  RemoveArrow,
  SelectConnection,
  SetSelectedTables,
  UpdateConnections,
  UpdateDB,
  UpdateSchema,
} from '../../redux/_Dataset/dataset.action';
import { fontSize } from '../..';
import CanvasColumn from './CanvasColumn';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { TDatabase } from '../../types/types';
import './dataset_common.css';
import { RootState } from '../../redux';
import { ISelectedTable } from '../../redux/_Dataset/types';
import { GoTable } from 'react-icons/go';
import { TableNode } from './Canvas';
import ShortUniqueId from 'short-unique-id';
import { ITable } from '../DataSet/types/BottomBarInterfaces';
import { FaCode } from 'react-icons/fa';
import ConfirmDialog from './ConfirmDialog';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

// helper function to get new table alias while duplicate table
const getDuplicateAlias = (originalTable: string, tables: ITable[]): string => {
  const copies = tables.filter((t) => t.table === originalTable);

  return `${originalTable} (${copies.length + 1})`;
};

function CanvasTable({ data }: NodeProps<TableNode>) {
  const { table, onTableUncheck, isConnecting, isInvalid } = data;
  const dispatch = useDispatch();
  const { selectedTables, color, canEdit, connections, arrows } = useSelector(
    (state: RootState) => ({
      selectedTables: state.dataset.selectedTables,
      color:
        state.dataset.connections.find(
          (connection) =>
            connection.id === table?.flatFileId || connection.id === table.connectionId,
        )?.color ?? '#2bb9bb',

      canEdit: state.dataset.canEdit,
      connections: state.dataset.connections,
      arrows: state.dataset.arrows,
    }),
  );
  const uid = new ShortUniqueId({ length: 4, dictionary: 'alpha' });
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const MAX_ALIAS_LENGTH = 40;
  const [isMaxLengthReached, setIsMaxLengthReached] = useState(false);
  const [renameInfo, setRenameInfo] = useState({
    isRename: false,
    alias: table.alias,
  });
  const [open, setOpen] = useState(false);
  const [hoverdTableId, sethoverdTableId] = useState<string | null>(null);

  const menuOptions = ['Remove', 'Rename', 'Duplicate'] as const;
  const handleClick = () => {
    dispatch(SelectConnection(table.isFlatFile ? table.flatFileId : table.connectionId));
    const schemaNeeded = !['mysql', 'mariadb', 'clickhouse'].includes(table.vendor);
    if (table.isFlatFile) return;
    let database = table.database;
    let schema = table.schema;
    if (table.isCustomQuery) {
      const relatedTable = selectedTables.find(
        (_table) => table.id !== _table.id && table.connectionId === _table.connectionId,
      );
      if (relatedTable) {
        database = relatedTable.database;
        schema = relatedTable.schema;
      }
    }
    dispatch(UpdateDB(table.connectionId, database));
    if (schemaNeeded) dispatch(UpdateSchema(table.connectionId, schema));
  };

  const handleRemoveTable = () => {
    const remainingTablesInGroup = selectedTables.filter(
      (t) => t.table === table.table && t.id !== table.id,
    );
    if (remainingTablesInGroup.length === 0) {
      onTableUncheck([table.id]);
    }
    if (table.isFlatFile && remainingTablesInGroup.length === 0)
      dispatch(
        UpdateConnections(connections.filter((connection) => connection.id !== table.flatFileId)),
      );
    const arrowIdsToBeDeleted = [
      ...new Set(
        arrows
          .filter((arrow) => arrow.startId === table.id || arrow.endId === table.id)
          .map((arrow) => arrow.arrowId),
      ),
    ];
    dispatch(RemoveArrow(arrowIdsToBeDeleted));

    dispatch(SetSelectedTables([...selectedTables.filter((_table) => _table.id !== table.id)]));
  };

  const handleOptionClick = (option: 'Rename' | 'Remove' | 'Duplicate') => {
    if (option === 'Remove') {
      if (!table.isNewTable) {
        setOpen(true);
        return;
      }
      handleRemoveTable();
    } else if (option === 'Duplicate') {
      const alias = getDuplicateAlias(table.table, selectedTables);
      const newId = uid();

      dispatch(duplicateTable(table.id, newId, alias));
    } else
      setRenameInfo({
        ...renameInfo,
        isRename: true,
      });

    setAnchorEl(null);
  };
  const handleRename = () => {
    dispatch(
      SetSelectedTables([
        ...selectedTables.map((_table) =>
          _table.id === table.id ? { ..._table, alias: renameInfo.alias } : _table,
        ),
      ]),
    );
    setRenameInfo({
      ...renameInfo,
      isRename: false,
    });
  };
  const sqlVendors: TDatabase[] = ['mysql', 'sqlserver', 'clickhouse'];
  const getTooltip = (table: ISelectedTable) => {
    return table.isWebApi
      ? `${table.table} (Web API)`
      : table.isFlatFile
        ? `${table.table} (Flatfile)`
        : table.isCustomQuery
          ? `${table.table} (Custom Query)`
          : sqlVendors.includes(table.vendor as TDatabase)
            ? `${table.table} (${table.database})`
            : `${table.table} (${table.schema})`;
  };
  return (
    <>
      <div
        onMouseEnter={() => sethoverdTableId(table.id)}
        onMouseLeave={() => sethoverdTableId(null)}
        style={{
          padding: '16px',
          backgroundColor: 'transparent',
          cursor: 'default',
        }}
      >
        <div
          className="draggableBox"
          onClick={handleClick}
          style={{
            width: '180px',
            borderRadius: '8px',
            backgroundColor: '#f8fafc',
            position: 'relative',
            // zIndex: table.order ?? 1,
            padding: 0,
          }}
        >
          <div
            style={{
              height: '8px',
              borderTopLeftRadius: '6px',
              borderTopRightRadius: '6px',
              backgroundColor: color,
              cursor: 'move',
            }}
          ></div>
          <div
            className="draggableBoxTitle"
            style={{ width: '100%', justifyContent: 'space-between', cursor: 'move' }}
            id={`ref-${table.id}`}
            onDoubleClick={() => {
              setRenameInfo({
                ...renameInfo,
                isRename: true,
              });
            }}
          >
            {renameInfo.isRename ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: 'auto',
                }}
              >
                <Tooltip
                  open={isMaxLengthReached}
                  title={`Maximum ${MAX_ALIAS_LENGTH} characters allowed`}
                  placement="top"
                  slotProps={{
                    tooltip: {
                      sx: { fontSize: '12px' },
                    },
                  }}
                >
                  <TextField
                    autoFocus={true}
                    variant="standard"
                    id="name"
                    value={renameInfo.alias}
                    inputProps={{ maxLength: MAX_ALIAS_LENGTH }}
                    InputProps={{
                      style: {
                        fontSize: '12px',
                        borderBottom: '1px solid gray',
                        width: '133px',
                      },
                    }}
                    sx={{
                      '& .MuiInputBase-root:before': {
                        display: 'none',
                      },
                    }}
                    onChange={(e) => {
                      const value = e.target.value;
                      setRenameInfo({ ...renameInfo, alias: value });
                      setIsMaxLengthReached(value.length >= MAX_ALIAS_LENGTH);
                    }}
                  />
                </Tooltip>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row-reverse',
                    columnGap: '5px',
                    marginTop: '0.5rem',
                    marginRight: '5px',
                  }}
                >
                  <Button
                    sx={{
                      color: 'gray',
                      fontSize: '11px',
                      border: '1px solid gray',
                      paddingBlock: '0.1rem',
                      height: '1.5rem',
                    }}
                    onClick={handleRename}
                  >
                    OK
                  </Button>
                  <Button
                    sx={{
                      color: 'gray',
                      fontSize: '11px',
                      border: '1px solid gray',
                      paddingBlock: '0.1rem',
                      height: '1.5rem',
                    }}
                    onClick={() => {
                      // setInputField(false);
                      // setNewName('');
                      setRenameInfo({
                        isRename: false,
                        alias: table.alias,
                      });
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <Tooltip
                  title={isMaxLengthReached ? table.alias : getTooltip(table)}
                  enterDelay={400}
                  placement="top"
                  slotProps={{
                    tooltip: {
                      sx: { fontSize: '12px' },
                    },
                  }}
                >
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: '5px', maxWidth: '130px' }}
                  >
                    {table.isCustomQuery ? (
                      <FaCode color="#494e6a" width={20} />
                    ) : (
                      <GoTable color="#494e6a" style={{ fontWeight: 'bold' }} />
                    )}
                    <p
                      className="ellipsis"
                      style={{ width: '100%', margin: 0, fontSize: fontSize.medium }}
                    >
                      {table.alias}
                    </p>
                  </div>
                </Tooltip>
                <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                  {isInvalid && (
                    <Tooltip
                      title="This table is invalid, please select new table from Select data nodes."
                      placement="top"
                      slotProps={{
                        tooltip: {
                          sx: { fontSize: '12px' },
                        },
                      }}
                    >
                      <WarningAmberIcon
                        sx={{
                          color: 'orange',
                          fontSize: '16px',
                          cursor: 'help',
                        }}
                      />
                    </Tooltip>
                  )}
                  <div
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => {
                      setAnchorEl(e.currentTarget);
                    }}
                  >
                    <MoreVertIcon
                      style={{ float: 'right', color: '#494e6a' }}
                      titleAccess={table.table.replace(/{}/g, '')}
                    />
                  </div>
                </div>
              </>
            )}
            {anchorEl && (
              <Menu anchorEl={anchorEl} open onClose={() => setAnchorEl(null)}>
                {menuOptions.map((option, idx) => {
                  if (isInvalid && (option === 'Rename' || option === 'Duplicate')) {
                    return null;
                  }
                  return (
                    <MenuItem
                      key={idx}
                      onClick={() => {
                        handleOptionClick(option);
                        setAnchorEl(null);
                      }}
                      disabled={table.isCustomQuery && option === 'Duplicate'}
                    >
                      {option}
                    </MenuItem>
                  );
                })}
              </Menu>
            )}
          </div>
          <div
            data-no-pan
            className="canvas-table-columns-scroll nowheel"
            style={{
              // max width - menubar, action bar, table card's header
              maxHeight: 'calc(100vh - 42px - 3rem - 90px)',
              overflowY: 'auto',
              scrollbarWidth: 'none',
              overflowX: 'hidden',
              paddingInline: '7px',
              marginInline: '-7px',
            }}
          >
            
            {table.columns.map((column, idx) => (
              <CanvasColumn
                hoverdTableId={hoverdTableId}
                isConnecting={isConnecting}
                column={column}
                tableId={table.id}
                schema={table.schema}
                database={table.database}
                fromCustomQueryTable={table.isCustomQuery}
                idx={idx}
                tablename={table.table}
                color={color}
                connectionId={table.connectionId}
                isFlatFile={table.isFlatFile ?? false}
                isWebApi={table.isWebApi ?? false}
              />
            ))}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={open}
        title="Remove Table?"
        message="Removing this table may cause issues in the dependant playbooks of this dataset. Are you sure?"
        confirmText="Remove"
        cancelText="Cancel"
        onConfirm={() => {
          handleRemoveTable();
          setOpen(false);
        }}
        onCancel={() => setOpen(false)}
      />
    </>
  );
}
export default CanvasTable;
